import type { ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { CloseIcon } from '@utils/icons';
import styles from './Drawer.module.css';

export interface IDrawerProps {
  /** Called on the backdrop click, the corner close button, and Escape. */
  onClose: () => void;
  /** Accessible name for the dialog (`aria-label`). */
  ariaLabel: string;
  /** Accessible label for the corner close button. */
  closeLabel: string;
  /** Panel content. */
  children: ReactNode;
  /** Extra class on the sliding panel (e.g. to constrain its width). */
  panelClassName?: string;
  /**
   * Controlled open state, for callers that keep the drawer MOUNTED and flip
   * it — Base UI then plays the slide-out and unmounts the panel itself when
   * the transition finishes, so the caller never times anything. Callers that
   * simply mount/unmount the drawer (the dashboard's alert drawer) can omit
   * it: rendered means open, and unmounting skips the exit.
   */
  open?: boolean;
  /**
   * A second panel docked immediately LEFT of the drawer (the issue drawer's
   * "set locations" idiom) — pass a `DrawerCompanion`. Rendered INSIDE the
   * dialog popup: Base UI dismisses on any outside press and traps focus in
   * the popup, so a sibling panel would close the drawer on its first click.
   * The popup itself never scrolls (the companion hangs outside its box and
   * would be clipped) — the children sit in an inner scroller.
   */
  companion?: ReactNode;
}

/**
 * A right-side drawer shell on Base UI's Dialog: portalled backdrop + sliding
 * panel + corner close button, closing on backdrop-click / Escape / the
 * button. The library owns the hard part — focus trap, scroll lock, nested
 * dialogs, and keeping the panel mounted until its exit transition finishes —
 * which is exactly the machinery a hand-rolled shell kept getting wrong
 * (timers racing animations, state seams flashing the panel back mid-close).
 *
 * Enter/exit are CSS transitions on `[data-starting-style]` /
 * `[data-ending-style]`, the same idiom the repo's Base UI tooltips and
 * popovers use.
 *
 * Portalled to `<body>` — dashboard cards are positioned by react-grid-layout
 * with a CSS transform, which would otherwise break a `position: fixed`
 * overlay mounted inside one.
 */
export function Drawer({
  onClose,
  ariaLabel,
  closeLabel,
  children,
  panelClassName,
  open = true,
  companion,
}: IDrawerProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.overlay} />
        <Dialog.Popup
          className={`${styles.panel}${panelClassName ? ` ${panelClassName}` : ''}`}
          aria-label={ariaLabel}
        >
          {companion}
          <button type="button" className={styles.close} onClick={onClose} aria-label={closeLabel}>
            <CloseIcon size={18} />
          </button>
          <ScrollArea className={styles.panelScroll} viewportClassName={styles.panelBody}>
            {children}
          </ScrollArea>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
